import React, { useEffect, useState } from 'react';
import { apiService } from '../../services/api.service';

type AdminDeposit = {
    transactionId: string;
    username: string;
    userId: string;
    amount: number;
    status: 'pending' | 'pending_admin' | 'processing' | 'success' | string;
    bankCode?: string;
    bankAccount?: string;
    bankAccountName?: string;
    reportedTransferAmount?: number;
    reportedSenderAccountName?: string;
    reportedSenderAccountNumber?: string;
    reportedSenderBankName?: string;
    createdAt?: string;
    referenceCode?: string;
};

type PendingTransactionsResponse = {
    data: AdminDeposit[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
};

const formatAmount = (amount: number) => `${amount.toLocaleString('vi-VN')} đ`;
const formatDate = (value?: string) => value
    ? new Intl.DateTimeFormat('vi-VN', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value))
    : '-';

const statusLabel: Record<string, string> = {
    pending: 'Đang chờ',
    pending_admin: 'Chờ duyệt',
    processing: 'Đang xử lý',
    success: 'Thành công',
};

const AdminDepositPage: React.FC = () => {
    const [transactions, setTransactions] = useState<AdminDeposit[]>([]);
    const [referenceCodes, setReferenceCodes] = useState<Record<string, string>>({});
    const [loadingId, setLoadingId] = useState('');
    const [cancellingId, setCancellingId] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    const loadPending = async () => {
        setIsLoading(true);
        setError('');
        try {
            const query = new URLSearchParams({ page: String(page) });
            if (search) query.set('search', search);
            const response = await apiService.get<PendingTransactionsResponse | AdminDeposit[]>(`/transactions/admin/pending?${query.toString()}`);
            if (Array.isArray(response)) {
                setTransactions(response);
                setTotalPages(1);
            } else {
                setTransactions(response?.data || []);
                setTotalPages(response?.totalPages || 1);
            }
        } catch (requestError: any) {
            const responseMessage = requestError.response?.data?.message;
            setError(Array.isArray(responseMessage) ? responseMessage.join(', ') : responseMessage || 'Không tải được giao dịch chờ duyệt.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        void loadPending();
    }, [page, search]);

    const handleApprove = async (transaction: AdminDeposit) => {
        setLoadingId(transaction.transactionId);
        setError('');
        setMessage('');
        try {
            const response = await apiService.post<{ message: string }>('/transactions/deposit/admin-confirm', {
                transactionId: transaction.transactionId,
                referenceCode: referenceCodes[transaction.transactionId] || undefined,
            });
            setMessage(`${response.message} Ví của ${transaction.username} đã được cộng ${formatAmount(transaction.amount)}.`);
            await loadPending();
        } catch (requestError: any) {
            const responseMessage = requestError.response?.data?.message;
            setError(Array.isArray(responseMessage) ? responseMessage.join(', ') : responseMessage || 'Không thể duyệt giao dịch.');
        } finally {
            setLoadingId('');
        }
    };

    const handleCancel = async (transaction: AdminDeposit) => {
        if (!window.confirm(`Hủy yêu cầu nạp ${formatAmount(transaction.amount)} của ${transaction.username}?`)) return;

        setCancellingId(transaction.transactionId);
        setError('');
        setMessage('');
        try {
            const response = await apiService.post<{ message: string }>('/transactions/deposit/admin-cancel', {
                transactionId: transaction.transactionId,
            });
            setMessage(response.message || `Đã hủy giao dịch ${transaction.transactionId}.`);
            await loadPending();
        } catch (requestError: any) {
            const responseMessage = requestError.response?.data?.message;
            setError(Array.isArray(responseMessage) ? responseMessage.join(', ') : responseMessage || 'Không thể hủy giao dịch.');
        } finally {
            setCancellingId('');
        }
    };

    return (
        <div className="flex-1 overflow-y-auto bg-gray-50 p-4 md:p-8">
            <div className="mx-auto max-w-6xl space-y-5">
                <div className="flex flex-wrap items-end justify-between gap-3">
                    <div>
                        <p className="text-sm font-medium text-blue-600">Quản trị giao dịch</p>
                        <h2 className="mt-1 text-2xl font-semibold text-gray-900">Duyệt nạp tiền</h2>
                        <p className="mt-1 text-sm text-gray-500">Kiểm tra giao dịch ngân hàng trước khi cộng tiền vào ví thành viên.</p>
                    </div>
                    <button type="button" onClick={() => void loadPending()} disabled={isLoading} className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                        {isLoading ? 'Đang tải...' : 'Làm mới'}
                    </button>
                </div>

                {(message || error) && <div className={`rounded-xl px-4 py-3 text-sm ${error ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>{error || message}</div>}

                <form
                    onSubmit={(event) => {
                        event.preventDefault();
                        setPage(1);
                        setSearch(searchInput.trim());
                    }}
                    className="flex flex-col gap-2 sm:flex-row"
                >
                    <input
                        value={searchInput}
                        onChange={(event) => setSearchInput(event.target.value)}
                        placeholder="Tìm theo mã giao dịch"
                        className="flex-1 rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    />
                    <button type="submit" className="rounded-xl bg-blue-600 px-5 py-2 text-sm font-semibold text-white hover:bg-blue-700">
                        Tìm kiếm
                    </button>
                </form>

                {transactions.length === 0 ? (
                    <div className="rounded-2xl bg-white p-10 text-center text-sm text-gray-500 shadow-sm">Không có giao dịch đang chờ duyệt.</div>
                ) : (
                    <div className="space-y-4">
                        {transactions.map((transaction) => (
                            <article key={transaction.transactionId} className="rounded-2xl bg-white p-5 shadow-sm">
                                <div className="flex flex-wrap items-start justify-between gap-4">
                                    <div>
                                        <div className="flex flex-wrap items-center gap-2">
                                            <h3 className="font-semibold text-gray-900">{transaction.username}</h3>
                                            <span className="rounded-full bg-yellow-100 px-3 py-1 text-xs font-medium text-yellow-800">{statusLabel[transaction.status] || transaction.status}</span>
                                        </div>
                                        <p className="mt-1 text-xs text-gray-500">{transaction.transactionId} · {formatDate(transaction.createdAt)}</p>
                                    </div>
                                    <p className="text-xl font-semibold text-blue-600">{formatAmount(transaction.amount)}</p>
                                </div>
                                <div className="mt-4 grid gap-3 rounded-xl bg-gray-50 p-4 text-sm sm:grid-cols-3">
                                    <div><p className="text-gray-500">Ngân hàng</p><p className="font-medium">{transaction.bankCode || '-'}</p></div>
                                    <div><p className="text-gray-500">Tài khoản nhận</p><p className="font-medium">{transaction.bankAccount || '-'}</p></div>
                                    <div><p className="text-gray-500">Chủ tài khoản</p><p className="font-medium">{transaction.bankAccountName || '-'}</p></div>
                                </div>
                                {transaction.reportedTransferAmount !== undefined && (
                                    <div className="mt-3 grid gap-3 rounded-xl border border-orange-200 bg-orange-50 p-4 text-sm sm:grid-cols-2">
                                        <div>
                                            <p className="text-orange-700">Số tiền khách báo đã chuyển</p>
                                            <p className="font-semibold text-gray-900">{formatAmount(transaction.reportedTransferAmount)}</p>
                                        </div>
                                        <div>
                                            <p className="text-orange-700">Chủ tài khoản gửi</p>
                                            <p className="font-semibold text-gray-900">{transaction.reportedSenderAccountName || '-'}</p>
                                        </div>
                                        <div>
                                            <p className="text-orange-700">Số tài khoản gửi</p>
                                            <p className="font-semibold text-gray-900">{transaction.reportedSenderAccountNumber || '-'}</p>
                                        </div>
                                        <div>
                                            <p className="text-orange-700">Ngân hàng gửi</p>
                                            <p className="font-semibold text-gray-900">{transaction.reportedSenderBankName || '-'}</p>
                                        </div>
                                    </div>
                                )}
                                <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                                    <input
                                        value={referenceCodes[transaction.transactionId] || ''}
                                        onChange={(event) => setReferenceCodes((current) => ({ ...current, [transaction.transactionId]: event.target.value }))}
                                        placeholder="Mã tham chiếu ngân hàng (không bắt buộc)"
                                        className="flex-1 rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                                    />
                                    <button type="button" onClick={() => void handleApprove(transaction)} disabled={loadingId === transaction.transactionId || cancellingId === transaction.transactionId} className="rounded-xl bg-green-600 px-5 py-2 font-semibold text-white hover:bg-green-700 disabled:opacity-50">
                                        {loadingId === transaction.transactionId ? 'Đang duyệt...' : 'Duyệt và cộng tiền'}
                                    </button>
                                    <button type="button" onClick={() => void handleCancel(transaction)} disabled={loadingId === transaction.transactionId || cancellingId === transaction.transactionId} className="rounded-xl border border-red-200 px-5 py-2 font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50">
                                        {cancellingId === transaction.transactionId ? 'Đang hủy...' : 'Hủy'}
                                    </button>
                                </div>
                            </article>
                        ))}
                    </div>
                )}

                {!isLoading && transactions.length > 0 && (
                    <div className="flex items-center justify-between text-sm text-gray-600">
                        <span>Trang {page} / {totalPages}</span>
                        <div className="flex gap-2">
                            <button type="button" disabled={page <= 1} onClick={() => setPage((value) => value - 1)} className="rounded-lg border bg-white px-3 py-1.5 disabled:opacity-40">
                                Trước
                            </button>
                            <button type="button" disabled={page >= totalPages} onClick={() => setPage((value) => value + 1)} className="rounded-lg border bg-white px-3 py-1.5 disabled:opacity-40">
                                Sau
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminDepositPage;
